import { createContext, useContext } from 'react';

import { BillingAccountV2, OrderTemplateDetail } from './api/types';

export type ShiftDraftV2 = {
  start_at: string;
  end_at: string;
  time_zone_offset: number;
  break_minutes?: number;
  quantity?: number;
};

export type JobDraftV2 = {
  address_id: number | null;
  contact_id: number | null;
  skill_id: number | null;
  uniform_id: number | null;
  quantity: number;
  job_type: 'GIG' | 'LTA';
  is_holiday: boolean;
  pay_rate: number;
  cost_rate: number;
  original_pay_rate: number;
  original_cost_rate: number;
  // True once the admin has typed in a rate field (markup Pay Rate step, or
  // the STATIC rate inputs on Schedule). GRAVY-10445: `pay_rate`/`cost_rate`
  // alone cannot tell "cleared on purpose" from "never quoted", so an emptied
  // field re-enabled the rate-quote prefill and the cached quote was written
  // straight back — backspacing the last digit looked like a no-op. Local to
  // the wizard; `buildCreatePayload` strips it before submit.
  pay_rate_touched?: boolean;
  // True once the admin has toggled a badge on this job. Same idea as
  // `pay_rate_touched` (GRAVY-10445): it is what tells "deliberately cleared"
  // apart from "not seeded yet", so the client's default badges are never
  // put back on a job the admin emptied on purpose. Stripped from the create
  // payload in `buildCreatePayload`.
  certificates_touched?: boolean;
  instructions: string;
  address_instructions: string;
  contact_instructions: string;
  uniform_instructions: string;
  mandatory_break_time: number;
  share_contact_number: boolean;
  all_shifts_same_worker: boolean;
  auto_accept_requests: boolean;
  certificates: number[];
  shifts: ShiftDraftV2[];
  staffing_strategy?: 'OPEN_JOB_POST' | 'SEND_INVITATIONS';
  invite_workers?: number[];
  hire_workers?: number[];
  // "Make a private job available to qualified workers if the selected
  // workers are unavailable" — corresponds to v1's PRIVATE + publish:true
  // semantics. The backend uses this to schedule a fallback PUBLIC
  // posting (+4h or +30m) after invites/hires complete, but only when
  // the job is still unfilled.
  auto_fill?: boolean;
  // Indexes (into `shifts`) the admin has deselected on the Staffing
  // invite sub-screen. Persisted so the wizard remembers the
  // selection across step navigation and so `buildCreatePayload` can
  // scope invites/hires to the still-selected shifts — V1 parity
  // (legacy applied hires only to selected schedules). Undefined or
  // empty means "all shifts selected", which keeps legacy single-
  // shift orders behaving exactly as before.
  unselected_shift_indexes?: number[];
  // Per-worker snapshot of the selected shift indexes (into `shifts`) at
  // the moment the admin hired/invited that worker. Keyed by worker id.
  // V1 parity: an assignment stays scoped to the shift selection that was
  // in effect when the Hire/Invite action was performed — selecting more
  // shifts afterwards must NOT retroactively copy/expand an existing
  // assignment onto the newly selected shifts. A missing entry means
  // "apply to all currently selected shifts", preserving legacy/template-
  // prefilled behavior where no per-worker scope was ever captured.
  worker_shift_scope?: Record<number, number[]>;
};

export type OrderStateV2 = {
  billingAccount: BillingAccountV2 | null;
  orderType: 'GIG' | 'LTA' | null;
  sourceTemplateId: number | null;
  isPrefilled: boolean;
  verificationEnabled: boolean;
  // Mirrors legacy gating: only show the QR check-in toggle when the
  // customer has `verification_enabled` turned on at the account level.
  verificationVisible: boolean;
  step: number;
  jobs: JobDraftV2[];
  // Snapshot of `jobs` taken when the user begins editing an existing
  // job from the Review step (`editJobDraft`). Non-null means an in-place
  // edit is in progress; a step-0 "Go back" restores this exactly,
  // mirroring how cancelNewJobDraft unwinds the add-skill path. Null
  // whenever no edit is in flight.
  editSnapshot: JobDraftV2[] | null;
  // Original position of the job being edited (its index in the array
  // before `editJobDraft` moved it to `jobs[0]` for the wizard). When
  // the edit finishes (Continue advances back into Review), the
  // modified `jobs[0]` is spliced back to this index so repeated edits
  // don't accumulate a reversed skill order across the repeat-order
  // flow. Null whenever no edit is in flight.
  editIndex: number | null;
  // True when the active `jobs[0]` is a freshly prepended draft from
  // "Add Skills" that has not yet been moved to the end of the array.
  // The wizard always reads/writes `jobs[0]`, so a new skill must
  // start there — but V1 parity (and template/repeat-order ordering)
  // requires the *completed* new skill to live at the end of `jobs`
  // (first-added first, last-added last). On Continue into Review we
  // rotate `jobs[0]` to the end and clear this flag; on cancel we
  // drop the prepended draft. Distinguishes "add-skill flow"
  // from "initial walk-through" / "edit-in-place" so the rotation
  // doesn't fire on a repeat-order template's first wizard pass.
  pendingNewJob: boolean;
  // The client's badge requirements (customer_certificates), as returned by
  // the certificates endpoint's `customer_defaults`. Held in state rather than
  // applied once on arrival because a job draft can be created BEFORE the
  // certificates query resolves (order type chosen after the client) and AFTER
  // it resolves (Add Skills) — in both cases the arrival-time effect has
  // already fired and will not fire again. Keeping the ids lets every draft
  // seed itself whenever it is created.
  //
  // Tagged with the billing account they came from rather than cleared when the
  // account changes. Clearing raced the response: the certificates arrive for
  // the newly-picked account, seed the draft, and THEN `setBillingAccount`'s
  // own update lands and wipes both the seeded job and the stored ids — after
  // which nothing re-fires and the badge never appears. Tagging makes a stale
  // client's ids inert without any destructive reset.
  customerDefaultCertificates: {
    billingAccountId: number | null;
    ids: number[];
  };
};

export type OrderActionsV2 = {
  setBillingAccount: (account: BillingAccountV2 | null) => void;
  setOrderType: (orderType: 'GIG' | 'LTA' | null) => void;
  applyTemplate: (template: OrderTemplateDetail) => void;
  clearTemplate: () => void;
  setStep: (step: number) => void;
  setJobs: (jobs: JobDraftV2[]) => void;
  // Prepends a fresh empty draft to `jobs` so the wizard (which always
  // edits `jobs[0]`) starts a new skill while previously completed jobs
  // are preserved at indices 1+. Mirrors v1's resetState + ADD_JOB split.
  addNewJobDraft: () => void;
  // Drops the in-progress draft at `jobs[0]` (used to bail out of an
  // additional skill the user started via "Add Skills" but no longer
  // wants). No-op when only one job exists, so existing single-skill
  // flows are unaffected.
  cancelNewJobDraft: () => void;
  // Move an existing job (by index) to `jobs[0]` so the wizard re-edits
  // it in place, pre-populated with the values it already carries (e.g. a
  // multi-skill template entry whose schedule/rates were never filled
  // in). Snapshots the pre-edit job list so cancelEditJobDraft can
  // restore it. Used by the Review step's per-skill "Edit" action.
  editJobDraft: (index: number) => void;
  // Restore the snapshot taken by editJobDraft (used by the step-0 "Go
  // back" while an in-place edit is in progress). No-op when no snapshot
  // is set.
  cancelEditJobDraft: () => void;
  // Finalize an in-place edit on Continue-into-Review: splice the
  // (possibly modified) `jobs[0]` back to the original `editIndex` so
  // the skill order matches the order the user originally created the
  // order in. Without this, every Review-step edit on a repeat order
  // would strand the picked job at index 0 and the persisted order
  // (and therefore the next repeat template) would drift toward
  // last-edited-first. No-op when no edit is in flight.
  finishEditJobDraft: () => void;
  // Finalize an add-skill flow: when `pendingNewJob` is true, rotate
  // `jobs[0]` (the freshly added skill) to the end of `jobs` so it
  // displays last on Review and serializes last in the submission /
  // template payload — V1 parity. No-op otherwise (initial flow,
  // repeat-order walk-through, edit-in-place). Called by the wizard
  // when Continue would advance into the Review step.
  finishNewJobDraft: () => void;
  setVerificationEnabled: (enabled: boolean) => void;
  setVerificationVisible: (visible: boolean, defaultEnabled: boolean) => void;
};

export const defaultJobDraftV2 = (orderType: 'GIG' | 'LTA'): JobDraftV2 => ({
  address_id: null,
  contact_id: null,
  skill_id: null,
  uniform_id: null,
  quantity: 1,
  job_type: orderType,
  is_holiday: false,
  pay_rate: 0,
  cost_rate: 0,
  original_pay_rate: 0,
  original_cost_rate: 0,
  pay_rate_touched: false,
  certificates_touched: false,
  instructions: '',
  address_instructions: '',
  contact_instructions: '',
  uniform_instructions: '',
  mandatory_break_time: 30,
  share_contact_number: false,
  all_shifts_same_worker: false,
  auto_accept_requests: false,
  certificates: [],
  shifts: [],
  staffing_strategy: orderType === 'GIG' ? 'OPEN_JOB_POST' : undefined,
  invite_workers: [],
  hire_workers: [],
  worker_shift_scope: {},
  auto_fill: false,
});

export const initialOrderStateV2: OrderStateV2 = {
  billingAccount: null,
  orderType: null,
  sourceTemplateId: null,
  isPrefilled: false,
  verificationEnabled: false,
  verificationVisible: false,
  step: 0,
  jobs: [],
  customerDefaultCertificates: { billingAccountId: null, ids: [] },
  editSnapshot: null,
  editIndex: null,
  pendingNewJob: false,
};

export const OrderStateContext =
  createContext<OrderStateV2>(initialOrderStateV2);
export const OrderActionsContext = createContext<OrderActionsV2 | null>(null);

export const useOrderStateV2 = () => useContext(OrderStateContext);

export const useOrderActionsV2 = () => {
  const value = useContext(OrderActionsContext);
  if (!value) {
    throw new Error(
      'useOrderActionsV2 must be used within OrderActionsContext',
    );
  }
  return value;
};
